// Small API client for the Stillroom backend.
const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;
export const APP_URL = process.env.REACT_APP_APP_URL || "";

/**
 * Stream a chat reply. Calls onDelta(text) as tokens arrive, onSentence(str)
 * when a full sentence has arrived (for sentence-by-sentence TTS), and
 * onDone(fullText) at completion. Returns an abort function.
 */
export function streamChat({ sessionId, mode, message, history, deviceId, onDelta, onSentence, onDone, onError }) {
  const ctrl = new AbortController();
  const IDLE_TIMEOUT_MS = 25000; // no activity for 25s → abort
  let idleTimer = null;
  let doneEmitted = false;

  const resetIdle = () => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      // Silent stall — usually a mobile-network intermediary killed the SSE.
      try {
        ctrl.abort();
      } catch {
        /* ignore */
      }
      if (!doneEmitted) {
        doneEmitted = true;
        onError && onError(new Error("stream_idle_timeout"));
      }
    }, IDLE_TIMEOUT_MS);
  };
  const clearIdle = () => {
    if (idleTimer) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }
  };

  (async () => {
    try {
      resetIdle();
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, mode, message, history, device_id: deviceId }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) {
        throw new Error(`stream failed: ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let full = "";
      let pending = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        resetIdle();
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) >= 0) {
          const raw = buf.slice(0, idx).trim();
          buf = buf.slice(idx + 2);
          if (!raw || raw.startsWith(":")) continue; // comment / heartbeat
          if (!raw.startsWith("data:")) continue;
          const payload = raw.slice(5).trim();
          if (!payload) continue;
          let ev;
          try {
            ev = JSON.parse(payload);
          } catch {
            continue;
          }
          if (ev.error) {
            clearIdle();
            doneEmitted = true;
            onError && onError(new Error(ev.error));
            return;
          }
          if (ev.done) {
            if (pending.trim() && onSentence) onSentence(pending.trim());
            clearIdle();
            doneEmitted = true;
            onDone && onDone(ev.full || full);
            return;
          }
          if (ev.delta) {
            full += ev.delta;
            pending += ev.delta;
            onDelta && onDelta(ev.delta, full);
            while (true) {
              const m = pending.match(/[^\.!\?…]+[\.!\?…]+["')\]]*(\s+|$)/);
              if (!m) break;
              const s = m[0].trim();
              pending = pending.slice(m[0].length);
              if (s && onSentence) onSentence(s);
            }
          }
        }
      }
      // Reader closed without a "done" event — treat as truncation.
      if (!doneEmitted) {
        clearIdle();
        if (pending.trim() && onSentence) onSentence(pending.trim());
        if (full.trim()) {
          doneEmitted = true;
          onDone && onDone(full);
        } else {
          doneEmitted = true;
          onError && onError(new Error("stream_closed_without_reply"));
        }
      }
    } catch (err) {
      clearIdle();
      if (doneEmitted) return;
      doneEmitted = true;
      if (err.name === "AbortError") return; // already surfaced via idle path
      onError && onError(err);
    }
  })();

  return () => {
    clearIdle();
    try {
      ctrl.abort();
    } catch {
      /* ignore */
    }
  };
}

export async function classify(history) {
  const res = await fetch(`${API}/chat/classify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ history }),
  });
  if (!res.ok) return { decision: "CONTINUE" };
  return res.json();
}

export async function generateInsight({ sessionId, mode, history, deviceId }) {
  const res = await fetch(`${API}/insight`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: sessionId, mode, history, device_id: deviceId }),
  });
  if (!res.ok) throw new Error("insight failed");
  return res.json();
}

export async function ttsBlob(text, voice = "shimmer") {
  const res = await fetch(`${API}/tts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text, voice }),
  });
  if (!res.ok) throw new Error("tts failed");
  return res.blob();
}

export async function transcribe(blob, filename = "audio.webm") {
  const fd = new FormData();
  fd.append("file", blob, filename);
  const res = await fetch(`${API}/stt`, { method: "POST", body: fd });
  if (!res.ok) throw new Error("stt failed");
  return res.json();
}
